/**
 * Validação de nome de paciente vs. queixa clínica colada no campo de nome.
 *
 * Conservador de propósito: um nome brasileiro incomum (Maria das Dores,
 * Maria dos Remédios, Jean-Pierre, Quispe Sucaticona, 6+ sobrenomes) TEM
 * de passar. Só barramos frase clínica / resposta de follow-up.
 */

const SINGLE_LETTER_PARTICLES = /^[eE]$/;

const NAME_PART_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:['’-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/;

const NAME_PARTICLES = new Set([
  'de', 'da', 'do', 'dos', 'das', 'e', 'em', 'na', 'no', 'nos', 'nas',
  'di', 'del', 'della', 'van', 'von', 'bin', 'al',
]);

/**
 * Partículas que ficam minúsculas no title-case do nome. Set próprio (e não o
 * `NAME_PARTICLES`) para não mexer na heurística de frase clínica.
 */
const TITLECASE_PARTICLES = new Set([
  ...['de', 'da', 'do', 'dos', 'das', 'e', 'di', 'del', 'della', 'delle', 'degli'],
  ...['van', 'von', 'der', 'den', 'du', 'dal', 'la', 'le', 'y', 'bin', 'al'],
]);

/** Tokens que quase nunca são prenome/sobrenome e quase sempre são queixa. */
const CLINICAL_NEVER_NAME_TOKENS = new Set([
  'atestado', 'receita', 'sintoma', 'sintomas', 'queixa', 'consulta',
  'gripe', 'resfriado', 'febre', 'tosse', 'diarreia', 'enxaqueca', 'cefaleia',
  'garganta', 'transparente', 'esbranquicada', 'esbranquicado',
  'purulenta', 'purulento', 'amarelada', 'amarelado', 'esverdeada', 'esverdeado',
  'ozempic', 'mounjaro', 'novotram', 'tramadol', 'secrecao', 'corrimento',
  'virose', 'sinusite', 'amigdalite', 'otite', 'conjuntivite', 'cistite',
  'gastroenterite', 'covid', 'dengue',
  // Caso Lilian 14/09/2026: "Já me fez cair Lilian Vago…" colou a queixa no nome.
  'ja', 'me', 'fez', 'cair', 'caiu', 'senti',
]);

/**
 * Colocações clínicas (multi-palavra). NÃO usar token isolado "dor"/"dores":
 * "Maria das Dores" e "Dorival" são nomes reais.
 */
const CLINICAL_COLLOCATION_RES = [
  /\balem d[aeo]s?\b/,
  /\bno maximo\b/,
  /\bvez por (mes|semana|dia|ano)\b/,
  /\bpreciso de\b/,
  /\bquero (um|uma|atestado|receita)\b/,
  /\bestou com\b/,
  /\bestou sentindo\b/,
  /\bsinto (dor|febre|falta|nause)/,
  /\bdor d[eao]s? (garganta|cabeca|costas|barriga|ouvido|dente|peito|olho|coluna|lombar)/,
  /\bdor nas? (costas|pernas|juntas|articulacoes|maos|unhas)/,
  /\bfalta de ar\b/,
  /\bmeio transparente\b/,
  /\besbranquicad/,
  /\bquando percebo\b/,
  /\bquando (sinto|preciso|uso|tomo)\b/,
  /\bolho (direito|esquerdo|vermelho)/,
  /\brenovacao de\b/,
  /\bja me fez\b/,
  /\bja me\b/,
  /\bme fez cair\b/,
  /\bme fez\b/,
  /\bfez cair\b/,
  /\bquase ca[ií]\b/,
];

const SENTENCE_OPENER_RE =
  /^(quando|porque|por que|onde|como|quanto|estou|sinto|preciso|quero|percebo|tenho que|gostaria|ja me)\b/;

const PLACEHOLDER_NAME_RE = /^(paciente|unknown|undefined|null|n\/a)$/i;
const AUTO_PATIENT_ID_RE = /^patient-\d{6,}(?:-[a-z0-9]+)*$/i;

export function foldPtName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function nameWordCount(value) {
  return String(value || '').trim().split(/\s+/).filter(Boolean).length;
}

export function isGenericPlaceholderName(value) {
  const raw = String(value || '').trim();
  if (!raw) return true;
  if (PLACEHOLDER_NAME_RE.test(raw)) return true;
  if (AUTO_PATIENT_ID_RE.test(raw)) return true;
  return false;
}

export function isValidSignupNamePart(part) {
  const p = String(part || '');
  if (!NAME_PART_RE.test(p)) return false;
  const letters = p.replace(/[-'’]/g, '');
  return letters.length >= 2 || SINGLE_LETTER_PARTICLES.test(p);
}

/** Formato do NameForm: 2+ palavras, só letras/hífen, partícula "e" de 1 letra. */
export function isValidSignupFullNameShape(value) {
  const name = String(value || '').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return false;
  return parts.every(isValidSignupNamePart);
}

/**
 * Title-case pt-BR do nome que sai no atestado e na receita.
 *
 * Mantém as partículas em minúsculas no meio do nome ("Maria da Silva dos
 * Santos", não "Maria Da Silva Dos Santos") e capitaliza subpalavras separadas
 * por hífen ou apóstrofo ("Sant'Anna", "D'Ávila", "Jean-Pierre"). A primeira e
 * a última palavra nunca viram minúsculas — sobrenome não é só partícula.
 */
export function normalizeSignupFullName(name) {
  const capSubparts = (word) =>
    String(word || '')
      .split(/([-'’])/)
      .map((seg) =>
        /^[-'’]$/.test(seg)
          ? seg
          : (seg[0] ? seg[0].toLocaleUpperCase('pt-BR') : '') +
            (seg.slice(1) ? seg.slice(1).toLocaleLowerCase('pt-BR') : ''),
      )
      .join('');

  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .map((w, idx) => {
      const lower = w.toLocaleLowerCase('pt-BR');
      const isParticle =
        idx > 0 && idx < words.length - 1 && TITLECASE_PARTICLES.has(foldPtName(lower));
      return isParticle ? lower : capSubparts(w);
    })
    .join(' ');
}

export function looksLikeClinicalPhraseName(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;

  if (/\d/.test(raw)) return true;
  // Só pontuação de frase/sintoma. Vírgula, parêntese e apóstrofo
  // aparecem em nomes reais ("Silva, Filho", "João (Neto)", "D'Ávila").
  if (/[;:!?/\\]/.test(raw)) return true;

  const folded = foldPtName(raw);
  if (!folded) return false;

  if (SENTENCE_OPENER_RE.test(folded)) return true;

  for (const re of CLINICAL_COLLOCATION_RES) {
    if (re.test(folded)) return true;
  }

  const tokens = folded.split(' ').filter(Boolean);
  const content = tokens.filter((t) => !NAME_PARTICLES.has(t));
  const clinicalHits = content.filter((t) => CLINICAL_NEVER_NAME_TOKENS.has(t));
  if (clinicalHits.length >= 2) return true;
  if (content.length >= 2 && clinicalHits.length === content.length) return true;

  if (
    tokens.length >= 8 &&
    /\b(quando|percebo|precisa|alem|maximo|sintoma|queixa|atestado|receita)\b/.test(folded)
  ) {
    return true;
  }

  return false;
}

export function isValidSignupFullName(value) {
  return isValidSignupFullNameShape(value) && !looksLikeClinicalPhraseName(value);
}

export function signupFullNameErrorReason(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'shape';
  if (/\d/.test(raw)) {
    return looksLikeClinicalPhraseName(raw.replace(/\d+/g, ' ')) ? 'clinical' : 'digits';
  }
  if (looksLikeClinicalPhraseName(raw)) return 'clinical';
  if (!isValidSignupFullNameShape(raw)) return 'shape';
  return null;
}

export const SIGNUP_NAME_ERROR_COPY = {
  clinical: 'Isso parece um sintoma ou queixa. Digite o nome completo da pessoa, como no documento.',
  shape: 'Digite o nome completo, sem abreviações.',
  digits: 'O nome não pode ter números. Digite o nome completo, como no documento.',
};

/**
 * Nome mínimo aceitável no chat/triagem.
 * Mais permissivo que o NameForm (aceita pontuação residual de OCR),
 * mas recusa placeholder e frase clínica.
 */
export function isPlausiblePatientFullName(name) {
  const raw = String(name || '').trim();
  if (raw.length < 5) return false;
  if (!/\s/.test(raw)) return false;
  if (isGenericPlaceholderName(raw)) return false;
  if (looksLikeClinicalPhraseName(raw)) return false;
  return true;
}

export function isPatientNameIntakeQuestion(text) {
  const folded = foldPtName(text);
  if (!folded) return false;
  if (/\b(medicamento|remedio|farmaco|principio ativo|exame|documento|anexo)\b/.test(folded)) {
    return false;
  }
  return (
    /qual e o (seu )?nome completo/.test(folded) ||
    /qual o (seu )?nome completo/.test(folded) ||
    /nome completo do paciente/.test(folded) ||
    /nome completo de quem (sera|vai ser) atendid/.test(folded)
  );
}

export function canPersistAnswerAsPatientName(question, answer) {
  return isPatientNameIntakeQuestion(question) && isPlausiblePatientFullName(answer);
}

/**
 * Só sobrescreve o nome do canal quando o candidato é um nome de pessoa
 * e o servidor está vazio, é placeholder, ou é queixa. Nunca troca um
 * nome plausível por uma frase clínica só porque ela é mais longa.
 */
export function shouldOverwritePatientName(serverName, candidateName) {
  const server = String(serverName || '').trim();
  const candidate = String(candidateName || '').trim();
  if (!candidate) return false;
  if (looksLikeClinicalPhraseName(candidate)) return false;
  if (!isPlausiblePatientFullName(candidate)) return false;

  const serverEmpty = !server || isGenericPlaceholderName(server);
  if (serverEmpty) return true;

  if (looksLikeClinicalPhraseName(server)) return true;

  if (isPlausiblePatientFullName(server)) {
    const sw = nameWordCount(server);
    const cw = nameWordCount(candidate);
    return cw > sw || candidate.length > server.length + 3;
  }

  return true;
}

export function payerNameConflictsWithClinicalCadastro(patientName, payerName) {
  const patient = String(patientName || '').trim();
  const payer = String(payerName || '').trim();
  if (!looksLikeClinicalPhraseName(patient)) return false;
  if (!isPlausiblePatientFullName(payer)) return false;
  return foldPtName(patient) !== foldPtName(payer);
}

export function clinicalNameWarningTitle(patientName, payerName) {
  const base =
    'Este nome parece queixa clínica, não o nome da pessoa. Corrija antes de emitir documento.';
  if (payerNameConflictsWithClinicalCadastro(patientName, payerName)) {
    return `${base} O pagador do PIX/cartão consta como "${String(payerName).trim()}".`;
  }
  return base;
}
